import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Upload, Trash2, FileText, Image, File as FileIcon, FolderOpen, Film, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { FileUpload } from '@/components/FileUpload';
import { DocumentPreviewModal } from '@/components/DocumentPreviewModal';
import { SearchableSelect } from '@/components/SearchableSelect';
import api from '@/lib/api';

const CATEGORIES = ['Permits', 'Contracts', 'Plans', 'Financial', 'Photos', 'Internal Notes', 'Other'];
const categoryIcons = { Permits: FileText, Contracts: FileText, Plans: FileText, Financial: FileText, Photos: Image, 'Internal Notes': FileText, Other: FileIcon };

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getTypeIcon(contentType) {
  if (!contentType) return FileIcon;
  if (contentType.startsWith('image/')) return Image;
  if (contentType.startsWith('video/')) return Film;
  if (contentType.includes('pdf') || contentType.includes('document')) return FileText;
  return FileIcon;
}

export default function DocumentsPage() {
  const [allDocuments, setAllDocuments] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [form, setForm] = useState({});
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const savedRef = useRef(false);

  const fetchDocs = () => {
    api.get('/documents').then(r => setAllDocuments(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetchDocs();
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    api.get('/clients').then(r => setClients(r.data)).catch(() => {});
  }, []);

  const documents = category === 'all' ? allDocuments : allDocuments.filter(d => d.category === category);

  const openUpload = () => {
    setForm({ name: '', category: 'Other', project_id: '', client_id: '', expiration_date: '' });
    setUploadedFile(null);
    savedRef.current = false;
    setSheetOpen(true);
  };

  const cleanupPendingUpload = (fileData) => {
    if (fileData?.file_id) {
      api.delete(`/upload/${fileData.file_id}`).catch(() => {});
    }
  };

  const handleSheetClose = (open) => {
    if (!open && uploadedFile && !savedRef.current) {
      cleanupPendingUpload(uploadedFile);
    }
    if (!open) {
      setUploadedFile(null);
      savedRef.current = false;
    }
    setSheetOpen(open);
  };

  const handleUpload = async () => {
    if (!form.name) return toast.error('Document name required');
    try {
      const payload = {
        ...form,
        file_url: uploadedFile?.file_url || '',
        file_id: uploadedFile?.file_id || '',
        file_name: uploadedFile?.file_name || '',
        file_size: uploadedFile?.file_size || 0,
        content_type: uploadedFile?.content_type || '',
      };
      await api.post('/documents', payload);
      toast.success('Document created');
      savedRef.current = true; // mark as saved so handleSheetClose doesn't delete
      setUploadedFile(null);
      setSheetOpen(false);
      fetchDocs();
    } catch (e) { toast.error('Error creating document'); }
  };

  const deleteDoc = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success('Document deleted');
      fetchDocs();
      if (previewDoc?.id === id) setPreviewDoc(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error deleting document'); }
  };

  // Category folder counts from all documents
  const catCounts = CATEGORIES.reduce((acc, c) => {
    acc[c] = allDocuments.filter(d => d.category === c).length;
    return acc;
  }, {});

  const filtered = documents.filter(d =>
    (d.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.file_name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4 animate-fade-in" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-baseline gap-3 text-[22px] font-medium tracking-tight">Documents<span className="text-sm text-muted-foreground/70 font-normal tabular-nums">{allDocuments.length}</span></h1>
        </div>
        <Button onClick={openUpload} className="bg-primary text-primary-foreground" data-testid="upload-document-button">
          <Upload className="h-4 w-4 mr-1" /> Upload Document
        </Button>
      </div>

      {/* Category Folders */}
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-2">
        <Card
          className={`border-border shadow-none cursor-pointer transition-colors ${category === 'all' ? 'bg-primary/10 border-primary/30' : 'bg-card hover:bg-secondary/30'}`}
          onClick={() => setCategory('all')}
        >
          <CardContent className="p-3 text-center">
            <FolderOpen className="h-5 w-5 mx-auto text-primary mb-1" />
            <p className="text-xs font-semibold">All</p>
            <p className="text-xs text-muted-foreground">{allDocuments.length}</p>
          </CardContent>
        </Card>
        {CATEGORIES.map(c => {
          const Icon = categoryIcons[c] || FileIcon;
          return (
            <Card
              key={c}
              className={`border-border shadow-none cursor-pointer transition-colors ${category === c ? 'bg-primary/10 border-primary/30' : 'bg-card hover:bg-secondary/30'}`}
              onClick={() => setCategory(c)}
            >
              <CardContent className="p-3 text-center">
                <Icon className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
                <p className="text-xs font-semibold truncate">{c}</p>
                <p className="text-xs text-muted-foreground">{catCounts[c] || 0}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search documents..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="documents-search" />
      </div>

      <div className="border border-border rounded-md bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">File</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Category</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Size</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Uploaded</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(d => {
              const TypeIcon = getTypeIcon(d.content_type);
              return (
                <TableRow key={d.id} className="border-border hover:bg-secondary/30 cursor-pointer" onClick={() => setPreviewDoc(d)} data-testid={`document-row-${d.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <TypeIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm">{d.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground truncate max-w-[150px]">{d.file_name || '-'}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{d.category}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatSize(d.file_size)}</TableCell>
                  <TableCell>
                    <div>
                      <p className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleDateString()}</p>
                      {d.uploaded_by_name && <p className="text-xs text-muted-foreground/60">{d.uploaded_by_name}</p>}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{d.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-0.5">
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-primary" onClick={(e) => { e.stopPropagation(); setPreviewDoc(d); }} data-testid={`preview-doc-${d.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={(e) => deleteDoc(d.id, e)} data-testid={`delete-doc-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No documents found</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Sheet open={sheetOpen} onOpenChange={handleSheetClose}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">Upload Document</SheetTitle>
            <SheetDescription>Attach a file and add metadata. Max file size: 45MB.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            {/* File Upload */}
            <div>
              <Label className="text-xs text-muted-foreground">File</Label>
              {uploadedFile ? (
                <div className="flex items-center gap-2 p-2 border border-emerald-500/20 rounded-md bg-emerald-500/5 mt-1" data-testid="uploaded-file-info">
                  <FileText className="h-4 w-4 text-emerald-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{uploadedFile.file_name}</p>
                    <p className="text-xs text-muted-foreground">{formatSize(uploadedFile.file_size)}</p>
                  </div>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" onClick={() => { cleanupPendingUpload(uploadedFile); setUploadedFile(null); }} data-testid="clear-uploaded-file">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <FileUpload
                  onUploadComplete={(data) => {
                    setUploadedFile(data);
                    if (!form.name && data.file_name) {
                      const nameWithoutExt = data.file_name.replace(/\.[^/.]+$/, '');
                      setForm(f => ({ ...f, name: nameWithoutExt }));
                    }
                  }}
                  className="mt-1"
                />
              )}
            </div>

            <div><Label className="text-xs text-muted-foreground">Document Name *</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-border" data-testid="document-form-name" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <Select value={form.category || 'Other'} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Project</Label>
              <SearchableSelect
                value={form.project_id || ''}
                onValueChange={v => setForm({ ...form, project_id: v })}
                options={[{ value: '', label: '-- None --' }, ...projects.map(p => ({ value: p.id, label: `${p.project_number} - ${p.name}` }))]}
                placeholder="Select project"
                searchPlaceholder="Search projects..."
                data-testid="document-form-project"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Client</Label>
              <SearchableSelect
                value={form.client_id || ''}
                onValueChange={v => setForm({ ...form, client_id: v })}
                options={[{ value: '', label: '-- None --' }, ...clients.map(c => ({ value: c.id, label: c.name }))]}
                placeholder="Select client"
                searchPlaceholder="Search clients..."
                data-testid="document-form-client"
              />
            </div>
            <div><Label className="text-xs text-muted-foreground">Expiration Date</Label><Input type="date" value={form.expiration_date || ''} onChange={e => setForm({ ...form, expiration_date: e.target.value })} className="bg-background border-border" /></div>
            <Button onClick={handleUpload} className="w-full bg-primary text-primary-foreground mt-2" data-testid="document-form-submit">Create Document</Button>
          </div>
        </SheetContent>
      </Sheet>

      <DocumentPreviewModal
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        doc={previewDoc}
      />
    </div>
  );
}
